import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@shared/ui/card";
import {
  BarChart,
  ResponsiveContainer,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

interface BillingTrendWidgetProps {
  consumerBillData?: any;
}

export const BillingTrendWidget = ({ consumerBillData }: BillingTrendWidgetProps) => {
  const { t } = useTranslation();

  const transformedBillData = useMemo(() => {
    if (!consumerBillData?.result?.billData) return [];
    if (!Array.isArray(consumerBillData?.result?.billData)) return [];
    
    return consumerBillData?.result?.billData?.map((bill: any) => ({
      name: bill.billMonth,
      billAmount: bill.billAmount,
      totalAmount: bill.totalAmountPayable,
    }));
  }, [consumerBillData]);

  return (
    <Card className="rounded-2xl shadow-sm border-slate-200">
          <div className="mb-4">
            <CardTitle className="text-lg font-bold text-slate-800">{t("Cost Over Time")}</CardTitle>        
            <CardDescription>
              {t("Track how your bills have changed over the last 6 months")}
            </CardDescription>
        </div>
      <CardContent className="px-2">
        <div className="h-[300px] flex items-center justify-center">
          {transformedBillData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={transformedBillData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value, name) => [`$${value}`, name]} />
                <Legend />
                <Bar 
                  dataKey="billAmount" 
                  name={t("Bill Amount")} 
                  fill="#8884d8" 
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-muted-foreground">
              {t("Loading bill data...")}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <div className="text-xs text-muted-foreground">
          <p>{t("Your bill amounts over the last 6 months.")}</p>
        </div>
      </CardFooter>
    </Card>
  );
};
